import React, { Fragment, useEffect, useState, useRef } from "react";
import { render } from "react-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store";
import { getProviderItemsData } from "./redux/slices/providerItemsSlice";
import { getAutoCompleteItemsData } from './redux/slices/autoCompleteSlice';
import { getSettings } from "../../../../../scripts/util/getSettings";
import { geoCode } from "../../../../../scripts/util/geoCode";
import ReactSelect from "../../../../../scripts/react/atoms/ReactSelect";
import { clickTrack } from "../../../../../scripts/util/clickTrack";
import { QsM, parseBoolean } from '../../../../../scripts/util/QsM';
import { qstr } from "../../../../../scripts/util/QsM";
import { scrollTopResults } from '../../../../../scripts/util/scrollTopResults';
import useOutsideClick from "../../../../../scripts/util/useOutsideClick";

import { ProviderContactCards } from "../../../../../../prototype/src/components/ProviderContactCards";
import { Pagination } from "../../../../../../prototype/src/components/Pagination";
import { Input } from "../../../../../../prototype/src/atoms/Input";
import { Button } from "../../../../../../prototype/src/atoms/Button";
import { Checkbox } from "../../../../../../prototype/src/atoms/Checkbox";
import shouldShowAcceptingPatients from '../../../../../../prototype/src/shared/shouldShowAcceptingPatients'

const pageSize = 12;
const specialties = getSettings(['vmc', 'fap', 'specialties']);
const locations = getSettings(['vmc', 'fap', 'locations']);
const affiliations = getSettings(['vmc', 'fap', 'affiliations']);
const genders = getSettings(['vmc', 'fap', 'genders']);
const languages = getSettings(['vmc', 'fap', 'languages']);
const featuredAffiliations = getSettings(['vmc', 'fap', 'featuredAffiliations']);
const placeHolderImagePath = getSettings(['vmc', 'fap', 'placeHolderImagePath']);
const sorts = [
  {
    label: "Name A-Z",
    value: "az",
  },
  {
    label: "Name Z-A",
    value: "za",
  },
  {
    label: "Near Me",
    value: "near",
  },
  {
    label: "Best Match",
    value: "best",
  }
];
const milesAway = [
  {
    label: "5 Miles",
    value: 5,
  },
  {
    label: "10 Miles",
    value: 10,
  },
  {
    label: "20 Miles",
    value: 20,
  },
  {
    label: "50 Miles",
    value: 50,
  },
  {
    label: "100 Miles",
    value: 100,
  },
];
const labels = getSettings(['vmc', 'fap', 'labels']);

const paramMap = {
  mi: 'miles',
  lt: 'latitude',
  ln: 'longitude',
  pg: 'page',
  st: 'sort',
  kw: 'search',
  sp: 'specialty',
  lo: 'location',
  gn: 'gender',
  af: 'affiliation',
  lg: 'language',
  nu: 'newPatients',
  os: 'openScheduling'
};

const getSingleSelectVal = (qsVal, selectVals) => {
  let initVal = selectVals.filter(x => x.value === qsVal);
  if (initVal && initVal.length > 0) {
    initVal = initVal[0];
  } else {
    initVal = undefined;
  }
  return initVal;
};

const getMultiSelectVal = (qsVal, selectVals) => {
  if (qsVal) {
    let initVal = [];
    for (const val of qsVal.split('|')) {
      initVal = initVal.concat(selectVals.filter(x => x.value === val));
    }
    return initVal;
  }
};

const initLat = Number(qstr('lt'));
const initLng = Number(qstr('ln'));
const initMiles = getSingleSelectVal(Number(qstr('mi')), milesAway);
const initAffiliation = getSingleSelectVal(Number(qstr('af')), affiliations);
const initGender = getSingleSelectVal(Number(qstr('gn')), genders);
const initLanguage = getSingleSelectVal(Number(qstr('lg')), languages);
const initSort = getSingleSelectVal(qstr('st'), sorts);
const initSpecialty = getMultiSelectVal(qstr('sp'), specialties);
const initLocation = getSingleSelectVal(qstr('lo'), locations);
const initNewPatients = parseBoolean(qstr('nu'));
const initOpenScheduling = parseBoolean(qstr('os'));
const initPage = Number(qstr('pg')) || 1;

const Component = () => {
  const [page, setPage] = useState(initPage || 1);
  const [search, setSearch] = useState(qstr('kw') || '');
  const [near, setNear] = useState('');
  const [specialty, setSpecialty] = useState(initSpecialty || []);
  const [location, setLocation] = useState(initLocation || []);
  const [miles, setMiles] = useState(initMiles || null);
  const [affiliation, setAffiliation] = useState(initAffiliation || null);
  const [gender, setGender] = useState(initGender || null);
  const [language, setLanguage] = useState(initLanguage || null);
  const [newPatients, setNewPatients] = useState(initNewPatients || false);
  const [openScheduling, setOpenScheduling] = useState(initOpenScheduling || false);
  const [sort, setSort] = useState(initSort || sorts.filter(option => option.value === 'az')[0]);
  const [latitude, setLatitude] = useState(initLat || null);
  const [longitude, setLongitude] = useState(initLng || null);
  const [oldData, setOldData] = useState(null);
  const [oldState, setOldState] = useState(null);
  const [pop, setPop] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const getState = () => {
    return {
      specialty: specialty ? specialty.reduce((ac, cv) => ac.concat(cv.value), []).join('|') : '',
      location: location ? location.value : null,
      sort: sort ? sort.value : null,
      miles: miles ? miles.value : null,
      affiliation: affiliation ? affiliation.value : null,
      gender: gender ? gender.value : null,
      language: language ? language.value : null,
      page,
      search,
      near,
      newPatients,
      openScheduling,
      latitude,
      longitude
    };
  };

  const providers = useSelector((state) => state.providerItems);
  const autoCompleteItems = useSelector((state) => state.autoCompleteItems);
  const dispatch = useDispatch();

  const previousController = useRef();
  const dispatchGetProviders = (geoLat, geoLng) => {
    //create parameter set
    const data = {
      keyword: search,
      size: pageSize,
      sort: sort.value,
      locationIds: location ? location.value : '',
      specIds: specialty ? specialty.map(x => x.value) : null,
      latitude: geoLat || latitude,
      longitude: geoLng || longitude,
      affiliation: affiliation ? affiliation.value : null,
      gender: gender ? gender.value : null,
      language: language ? language.value : null,
      newPatients: newPatients,
      openScheduling: openScheduling
    };
    if (miles) {
      data.miles = miles.value;
    };

    //compare old parameter set with new minus the paging parameter, if different then reset the paging
    const newData = JSON.stringify(data);
    if (newData === oldData || oldData === null) {
      data.start = (page - 1) * pageSize;
    } else {
      data.start = 0;
      setPage(1);
    }

    //store the old parameter set for later comparision
    setOldData(newData);

    if (previousController.current) {
      previousController.current.abort();
    }

    //call the api with the new parameter set
    const controller = new AbortController()
    previousController.current = controller;
    dispatch(getProviderItemsData(data, controller.signal));

    //cache the lat/lng if the source was from geocoding
    if (geoLat && geoLng) {
      setLatitude(geoLat);
      setLongitude(geoLng);
    }
  };

  const previousAcController = useRef();
  useEffect(() => {
    if (previousAcController.current) {
      previousAcController.current.abort();
    }
    if (search) {
      const controller = new AbortController()
      previousAcController.current = controller;
      dispatch(getAutoCompleteItemsData(search, controller.signal));
    } else {
      setSearchResults([]);
    }
  }, [search]);

  useEffect(() => {
    if (!autoCompleteItems.isFetching && autoCompleteItems.data.items) {
      setSearchResults(autoCompleteItems.data.items.map(item => item.query));
    }
  }, [autoCompleteItems]);

  useEffect(() => {
    if (!providers.isFetching) {
      if (providers.data.totalPages > 0 && page > providers.data.totalPages) {
        setPage(1);
        new QsM(paramMap).addParams([{ paramName: 'pg', paramValue: 1 }]).addFilters(getState()).doUpdate();
      }
    }
  }, [providers]);

  useEffect(() => {
    if (previousController.current) {
      previousController.current.abort();
    }
    dispatchGetProviders();
  }, [page, sort, pop, affiliation, gender, language, newPatients]);

  useEffect(async () => {
    //initial lat / lng
    if (initLat && initLng) {
      const geoCodeResults = await geoCode({
        location: {
          lat: initLat,
          lng: initLng
        }
      });
      for (const item of geoCodeResults) {
        if (item.types && item.types.indexOf('locality') > -1) {
          setNear(item.formatted_address);
        }
      }
    }

    window.onpopstate = event => {
      const evtState = event.state;
      if (evtState !== null) {
        setAffiliation(evtState.affiliation ? getSingleSelectVal(evtState.affiliation, affiliations) : null);
        setGender(evtState.gender ? getSingleSelectVal(evtState.gender, genders) : null);
        setLanguage(evtState.language ? getSingleSelectVal(evtState.language, languages) : null);
        setMiles(evtState.miles ? getSingleSelectVal(evtState.miles, milesAway) : null);
        setSort(evtState.sort ? getSingleSelectVal(evtState.sort, sorts) : null);
        setNewPatients(evtState.newPatients);
        setOpenScheduling(evtState.openScheduling);
        setLocation(evtState.location ? getSingleSelectVal(evtState.location, locations) : []);
        setSpecialty(evtState.specialty ? getMultiSelectVal(evtState.specialty, specialties) : []);
        setLatitude(evtState.latitude);
        setLongitude(evtState.longitude);
        setNear(evtState.near);
        setSearch(evtState.search);
        setPage(evtState.page);
        setPop(new Date().getTime());
      }
    };
    setOldState(getState());
  }, []);
  const resultRef = useRef();
  const searchClick = async () => {
    resultRef.current.scrollIntoView({ behavior: 'smooth' });
    setOldState(getState());
    if (search) {
      setSort(sorts.filter(option => option.value === 'best')[0]);
    }
    if (latitude === null && longitude === null && near) {
      const geoCodeResults = await geoCode({
        address: near
      });
      let geoLat = null;
      let geoLng = null;
      if (geoCodeResults && geoCodeResults.length > 0) {
        const geoCodeResult = geoCodeResults[0];
        geoLat = geoCodeResult.geometry.location.lat();
        geoLng = geoCodeResult.geometry.location.lng();
      }
      dispatchGetProviders(geoLat, geoLng);
      new QsM(paramMap).addParams(getSearchClickParams(geoLat, geoLng)).addFilters(oldState).doUpdate();
      return;
    }
    new QsM(paramMap).addParams(getSearchClickParams()).addFilters(oldState).doUpdate();
    dispatchGetProviders();
  };

  const getSearchClickParams = (geoLat, geoLng) => {
    const params = [];
    search && params.push({ paramName: 'kw', paramValue: search });
    specialty && params.push({ paramName: 'sp', paramValue: specialty.reduce((ac, cv) => ac.concat(cv.value), []).join('|') });
    location && params.push({ paramName: 'lo', paramValue: location.value === undefined ? '' : location.value });
    if (geoLat && geoLng) {
      params.push({ paramName: 'lt', paramValue: geoLat });
      params.push({ paramName: 'ln', paramValue: geoLng });
    } else {
      latitude && params.push({ paramName: 'lt', paramValue: latitude });
      longitude && params.push({ paramName: 'ln', paramValue: longitude });
    }
    miles && params.push({ paramName: 'mi', paramValue: miles.value });
    return params;
  };

  const locationIconClick = () => {
    navigator.geolocation.getCurrentPosition(async position => {
      if (position.coords && position.coords.latitude && position.coords.longitude) {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        const geoCodeResults = await geoCode({
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
        });
        for (const item of geoCodeResults) {
          if (item.types && item.types.indexOf('locality') > -1) {
            setNear(item.formatted_address);
          }
        }
      }
    });
  };

  const searchOnPaste = e => {
    const textData = e.clipboardData.getData('Text');
    const filtered = textData;//.replace(/[^A-Za-z0-9 ]/, '');
    if (textData.length !== filtered.length) {
      e.preventDefault();
    }
  };

  const searchOnKeyDown = e => {
    let key = e.key;
    key = key.replace(/[^A-Za-z0-9 ]/, '');
    if (key) {
      return;
    }
    e.preventDefault();
  };

  const searchOnKeyUp = e => {
    if (e.key === 'Enter') {
      searchClick();
    }
  };

  const searchFieldClick = e => {
    //setShowResults(true);
  };

  const handleAcClose = (() => {
    setShowResults(false);
  });

  const handleAcItemClick = e => {
    e.preventDefault();
    setSearch(e.target.textContent);
  };

  const ref = useRef();
  useOutsideClick(ref, () => {
    if (showResults) {
      setShowResults(false);
    }
  });

  const nearOnKeyUp = e => {
    if (e.key === 'Enter') {
      searchClick();
    }
  };

  const nearChange = e => {
    setNear(e.target.value);
    if (latitude && longitude) {
      setLatitude(null);
      setLongitude(null);
    }
  };

  const affiliationChange = value => {
    setAffiliation(value);
    if (value) {
      new QsM(paramMap).addParams([{ paramName: 'af', paramValue: value.value }]).addFilters(getState()).doUpdate();
    } else {
      new QsM(paramMap).addParams([{ paramName: 'af', paramValue: '' }]).addFilters(getState()).doUpdate();
    }
  };

  const genderChange = value => {
    setGender(value);
    if (value) {
      new QsM(paramMap).addParams([{ paramName: 'gn', paramValue: value.value }]).addFilters(getState()).doUpdate();
    } else {
      new QsM(paramMap).addParams([{ paramName: 'gn', paramValue: '' }]).addFilters(getState()).doUpdate();
    }
  };

  const languageChange = value => {
    setLanguage(value);
    if (value) {
      new QsM(paramMap).addParams([{ paramName: 'lg', paramValue: value.value }]).addFilters(getState()).doUpdate();
    } else {
      new QsM(paramMap).addParams([{ paramName: 'lg', paramValue: '' }]).addFilters(getState()).doUpdate();
    }
  };

  const newPatientsChange = value => {
    const newVal = !newPatients;
    setNewPatients(newVal);
    new QsM(paramMap).addParams([{ paramName: 'nu', paramValue: newVal }]).addFilters(getState()).doUpdate();
  };

  const sortChange = value => {
    setSort(value);
    new QsM(paramMap).addParams([{ paramName: 'st', paramValue: value.value }]).addFilters(getState()).doUpdate();
  };

  const prevOnClick = () => {
    setPage(page - 1);
    new QsM(paramMap).addParams([{ paramName: 'pg', paramValue: page - 1 }]).addFilters(getState()).doUpdate();
    scrollTopResults('.find_filter_result');
  };

  const nextOnClick = () => {
    setPage(page + 1);
    new QsM(paramMap).addParams([{ paramName: 'pg', paramValue: page + 1 }]).addFilters(getState()).doUpdate();
    scrollTopResults('.find_filter_result');
  };

  const pageOnClick = value => {
    setPage(value);
    new QsM(paramMap).addParams([{ paramName: 'pg', paramValue: value }]).addFilters(getState()).doUpdate();
    scrollTopResults('.find_filter_result');
  };

  const providerCards = () => {
    let providerData = providers.data.contactCardList;
    return providerData.map((item, i) => {
      let providerAffiliation;
      if (item.affiliation && item.affiliation.length > 0) {
        if (featuredAffiliations.filter(x => x.value === item.affiliation[0].id).length > 0) {
          providerAffiliation = item.affiliation[0].name;
        }
      }
      let showAcceptPatients = shouldShowAcceptingPatients(item);
      
      return (
        <ProviderContactCards
          key={i}
          name={item.name}
          title={item.title}
          titles={item.titles}
          specialties={item.specialities.map(x => x.name)}
          acceptPatients={item.acceptPatients}
          acceptPatientsOriginal={item.acceptPatientsOriginal}
          acceptPatientsOriginalBioDisplayText={item.acceptPatientsOriginalBioDisplayText}
          acceptPatientsOriginalSearchDisplayText={item.acceptPatientsOriginalSearchDisplayText}
          acceptPatientsOriginalImageURL={item.acceptPatientsOriginalImageURL}
          showAcceptPatients={showAcceptPatients}
          myChartProviderID={item.myChartProviderID}
          myChartDepartmentID={item.myChartDepartmentID}
          myChartVisitTypeID={item.myChartVisitTypeID}
          myChartSchedulingSearchResultsEnabled={window.myChartProviderConfig.openSchedulingSearchResultsEnabled}
          imageUrl={item.imageUrl}
          imageErrorUrl={placeHolderImagePath}
          affiliation={providerAffiliation}
          bioPageUrl={item.bioPageUrl}
          telehealthOptionsAvailable={item.telehealthOptionsAvailable}
          hideEarliestAppointment={item.hideEarliestAppointment}
          providerClick={event => {
            if (item.query) {
              event.preventDefault();
              clickTrack({
                Query: item.query,
                HitType: item.hitType,
                HitId: item.hitId,
                TrackId: item.trackId
              }, item.bioPageUrl);
            }
          }}
        />
      );
    });
  };

  return (
    <div className="find_provider_contact_page">
      <div className="find_wrapper">
        <div className="find_container">
          <div className="find_top_info">
            <h2>{labels.title}</h2>
            <div className="care_image_wrap">
              <img className="care_image" src="/assets/images/we-care.png" alt="" />
            </div>
          </div>
          <div className="find_bottom_info">
            <div className="find_form">
              <div className="find_top_form">
                <div className="inner_form_find_col_top1">
                  <div className="form_group">
                    <Input
                      className={"input_find_top1"}
                      placeholder={labels.searchPlaceholder}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      onPaste={searchOnPaste}
                      onKeyUp={searchOnKeyUp}
                      onKeyDown={searchOnKeyDown}
                      onClick={searchFieldClick}
                      maxLength={100} />
                    <img className="find_search_icon" src="/assets/images/icon-search-2.svg" alt="" />
                  </div>
                  {showResults && searchResults.length > 0 && <div className="autosuggestion_list" ref={ref}>
                    <img className="icon_close" src="/assets/images/icon-x-black.svg" alt="" onClick={handleAcClose} />
                    <div className="autosuggestion_inner">
                      <ul>
                        {searchResults.map((item, i) => (
                          <li key={i}><a href='#' onClick={handleAcItemClick}>{item}</a></li>
                        ))}
                      </ul>
                    </div>
                  </div>}
                </div>
              </div>
              <div className="find_bottom_form">
                <div className="inner_form_find_col_bottom1">
                  <div className="form_group custom_dropdown">
                    <ReactSelect
                      isMulti={true}
                      options={specialties}
                      className="default form_control custom_select"
                      placeholder={labels.specialtyPlaceholder}
                      value={specialty}
                      onChange={setSpecialty} />
                  </div>
                </div>
                <div className="inner_form_find_col_bottom2">
                  <div className="form_group">
                    <Input
                      className={"default"}
                      placeholder={labels.nearPlaceholder}
                      value={near}
                      onChange={nearChange}
                      onKeyUp={nearOnKeyUp}
                      maxLength={100} />
                    <img onClick={locationIconClick} className="find_location_icon" src="/assets/images/icon-location.svg" alt="" />
                  </div>
                </div>
                <div className="inner_form_find_col_bottom3">
                  <div className="form_group custom_dropdown">
                    <ReactSelect
                      isMulti={false}
                      options={milesAway}
                      className="default form_control custom_select"
                      placeholder={labels.milesAwayPlaceholder}
                      value={miles}
                      isSearchable={false}
                      isClearable={true}
                      onChange={setMiles} />
                  </div>
                </div>
              </div>
              <div className="find_submit_and_map_form">
                <div className="find_campus_map">
                  <p>Not sure where to go? See our <a href="/patients--visitors/maps" target="_blank" style={{ fontSize: '1.7rem' }}>campus maps</a>.</p>
                </div>
                <div className="find_buttons">
                <Button
                    className="primary"
                    label={labels.searchCTAText}
                    onClick={searchClick} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="find_content_bottom">
        <div className="find_mf">
          <div className="find_mf_form_wrapper">
            <div className="form_group more_filters find_mf_form_box">
              <Button className={"tertiary"} label={"More Filters"} />
            </div>
            <div className="find_mf_form">
              <div className="find_mf_form_box">
                <div className="form_group custom_dropdown">
                  <ReactSelect
                    isMulti={false}
                    options={affiliations}
                    className="default form_control custom_select"
                    placeholder={labels.providerAffiliationPlaceholder}
                    placeholderColor={'#7f8a8a'}
                    value={affiliation}
                    isSearchable={false}
                    isClearable={true}
                    onChange={affiliationChange}
                    style={'custom_dropdown'}
                    arrowColor={'grey'} />
                </div>
                <div className="tooltip_box">
                  <img className="find_info_icon" src="/assets/images/icon-info-2.svg" alt="" />
                  <span className="tooltip_text">{labels.providerAffiliationTooltipText}</span>
                </div>
              </div>
              <div className="find_mf_form_box">
                <div className="form_group custom_dropdown">
                  <ReactSelect
                    isMulti={false}
                    options={genders}
                    className="default form_control custom_select"
                    placeholder={labels.genderPlaceholder}
                    placeholderColor={'#7f8a8a'}
                    value={gender}
                    isSearchable={false}
                    isClearable={true}
                    onChange={genderChange}
                    style={'custom_dropdown'}
                    arrowColor={'grey'} />
                </div>
              </div>
              <div className="find_mf_form_box">
                <div className="form_group custom_dropdown">
                  <ReactSelect
                    isMulti={false}
                    options={languages}
                    className="default form_control custom_select"
                    placeholder={labels.languagePlaceholder}
                    placeholderColor={'#7f8a8a'}
                    value={language}
                    isClearable={true}
                    onChange={languageChange}
                    style={'custom_dropdown'}
                    arrowColor={'grey'} />
                </div>
              </div>
              <div className="find_mf_form_box">
                <div className="find_mf_form_checkbox">
                  <Checkbox
                    className={"default"}
                    label={labels.newPatientsPlaceholder}
                    checked={newPatients}
                    onChange={newPatientsChange} />
                </div>
              </div>
            </div>
            <div className="find_filter_result find_mf_form_wrapper">
              <div className="find_filter_count" ref={resultRef}>
                {!providers.isFetching && providers.data.success && providers.data.total > 0 && <Fragment>{providers.data.total} {labels.resultsLabel}</Fragment>}
                {!providers.isFetching && providers.data.success && providers.data.total === 0 && <Fragment>{labels.noResultsLabel}</Fragment>}
              </div>
              <div className="find_mf_form">
                <div className="find_mf_form_box">
                  <div className="form_group custom_dropdown">
                    <ReactSelect
                      isMulti={false}
                      options={sorts}
                      className="default form_control custom_select"
                      placeholder="Sort By"
                      placeholderColor={'#7f8a8a'}
                      value={sort}
                      isSearchable={false}
                      isClearable={false}
                      onChange={sortChange}
                      style={'custom_dropdown'}
                      arrowColor={'grey'} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={providers.data.total === 0 ? 'find_content no_results' : 'find_content'}>
          {!providers.isFetching && providers.data.success && providers.data.total > 0 &&
            <Pagination
              results={pageSize}
              currentPage={page}
              total={providers.data.total}
              totalPages={providers.data.totalPages}
              prevClick={prevOnClick}
              nextClick={nextOnClick}
              pageClick={pageOnClick} />}
          {providers.isFetching && <div className="spinner"><img src="/assets/images/loading.gif" alt="" /></div>}
          <div className="provider_contact_card_links">
            {!providers.isFetching && providers.data.success && providerCards()}
          </div>
          {!providers.isFetching && providers.data.success && providers.data.total > 0 &&
            <Pagination
              results={pageSize}
              currentPage={page}
              total={providers.data.total}
              totalPages={providers.data.totalPages}
              prevClick={prevOnClick}
              nextClick={nextOnClick}
              pageClick={pageOnClick} />}
        </div>
      </div>
    </div>
  );
};

render(
  <Provider store={store}>
    <Component />
  </Provider>,
  document.getElementById("findAProviderRoot")
);
